import { useState, Fragment, useEffect, useRef } from 'react';
import { Info } from 'lucide-react';

interface Props {
  text: string;
  isDark: boolean;
}

// Auto-linkify http(s) URLs inside tooltip text while preserving whitespace-pre-line.
function renderWithLinks(text: string, isDark: boolean) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  const linkCls = isDark ? 'text-cyan-400 hover:underline break-all' : 'text-cyan-600 hover:underline break-all';
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className={linkCls}>{part}</a>;
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

export default function InfoTooltip({ text, isDark }: Props) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false); // sticky after click — stays open for interaction
  const ref = useRef<HTMLSpanElement>(null);
  const visible = hovered || pinned;

  // Close pinned popup when clicking outside
  useEffect(() => {
    if (!pinned) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setPinned(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pinned]);

  return (
    <span ref={ref} className="relative inline-flex items-center">
      <button
        className={`ml-1.5 transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => { e.stopPropagation(); setPinned((v) => !v); }}
        aria-label="Info"
        aria-expanded={visible}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {visible && (
        <div
          className={`absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 sm:w-80 max-w-[calc(100vw-2rem)] p-3 rounded-lg border shadow-xl text-xs text-left whitespace-pre-line leading-relaxed ${
            isDark
              ? 'bg-slate-900 border-slate-600 text-slate-200'
              : 'bg-white border-gray-200 text-gray-700'
          }`}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {renderWithLinks(text, isDark)}
          <div
            className={`absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent ${
              isDark ? 'border-t-slate-600' : 'border-t-gray-200'
            }`}
          />
        </div>
      )}
    </span>
  );
}
